/**
 * A teacher's boxes in the person visit (P12 to P14): the teaching profile,
 * its facts saved where they are drawn; the sections they teach; and Remove
 * teaching profile, opened in place and blocked while a course refers to it
 * (F5). Add teaching profile is person management's one Task, which the
 * Teaching section draws for an instructor who signs in without a profile.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { Blocker, Box, FactBox, ListBox, Read, TaskBox } from "@/components/ui/box.schema";
import { currentOnConflict } from "@/lib/api/conflict";
import { facultyApi, type FacultyCourseLoad, type FacultyProfileWriteResult, type FacultyRemovalPreview } from "@/lib/api/faculty";
import { PEOPLE_QUERY_KEY, type PeopleDetailResponse, type PeopleFacultyProfile } from "@/lib/api/people";

import { peopleWords as words } from "../people-copy";
import { detailEligibleFacultyAccount } from "../person-record";
import { courseStatusWord } from "../teaching/course-status";
import { imam, queryRead, readOf, useFieldSaves, useTaskCommit } from "./commit";
import { resultState } from "./access-boxes";
import { savedField } from "./fields";
import { teachingCopy as copy, teachingText } from "./teaching-copy";

const box = <T,>(spec: FactBox<T> | ListBox<T, unknown> | TaskBox<T>) => spec as unknown as Box;
const OPEN = { does: "open", verb: words.open } as const;

const FIELDS = ["title_role", "specialization", "training_chain"] as const;
type TeachingField = (typeof FIELDS)[number];
type Profile = Pick<PeopleFacultyProfile, "id" | "updated_at" | TeachingField>;

const profileOf = (value: PeopleFacultyProfile | FacultyProfileWriteResult): Profile => ({
  id: value.id,
  updated_at: value.updated_at,
  title_role: value.title_role,
  specialization: value.specialization,
  training_chain: value.training_chain,
});

function assignmentFacts(course: FacultyCourseLoad): string {
  return [
    course.semester_name ?? copy.noTerm,
    course.crn ? teachingText.crn(course.crn) : null,
    course.assignment_kind ? (course.assignment_kind === "primary" ? copy.primary : copy.coInstructor) : null,
    courseStatusWord(course.status),
  ].filter(Boolean).join(" · ");
}

export function useTeachingBoxes(personId: string | null, detailRead: Read<PeopleDetailResponse>): Box[] {
  const client = useQueryClient();
  const detail = personId ? (detailRead.status === "ready" ? detailRead.value : detailRead.status === "stale" ? detailRead.last : null) : null;
  const profile = detail?.faculty_profile ?? null;
  const account = detail ? detailEligibleFacultyAccount(detail) : null;

  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
    client.invalidateQueries({ queryKey: ["faculty"] }),
  ]);
  // A removed profile may have been the only thing holding this person: the
  // result stays here, and Return reads the register again (O10).
  const refreshRegister = () => Promise.all([
    client.invalidateQueries({ queryKey: [...PEOPLE_QUERY_KEY, "register"] }),
    client.invalidateQueries({ queryKey: ["faculty"] }),
  ]);

  const preview = useQuery({
    queryKey: ["faculty", profile?.id ?? null, "removal-preview"],
    queryFn: () => facultyApi.removalPreview(profile?.id as string),
    enabled: Boolean(profile),
    retry: false,
  });

  const saves = useFieldSaves<Profile, TeachingField>({
    record: profile ? profileOf(profile) : null,
    identity: (value) => value.id,
    version: (value) => value.updated_at,
    fields: FIELDS,
    read: (value, field) => value[field] ?? "",
    send: async (field, value, base, key) =>
      profileOf(await facultyApi.patch(base.id, { [field]: value.trim() === "" ? null : value, expected_updated_at: base.updated_at }, key)),
    conflictOf: (error) => {
      const current = currentOnConflict<FacultyProfileWriteResult>(error);
      return current ? profileOf(current) : null;
    },
    reread: async () => {
      await refresh();
      const fresh = client.getQueryData<PeopleDetailResponse>([...PEOPLE_QUERY_KEY, "detail", personId, null]);
      if (!fresh?.faculty_profile) throw new Error("unread");
      return profileOf(fresh.faculty_profile);
    },
    onSaved: () => void refresh(),
  });

  const removing = useTaskCommit({
    subject: `person:${personId ?? "none"}`,
    words: { refused: copy.removeRefused, unknown: copy.removeUnknown, unknownSentence: copy.unknownSentence, checkNote: copy.removeCheckNote },
    refresh: refreshRegister,
  });

  if (!personId || !detail || (!profile && !account)) return [];
  const at = (id: string) => `${id}-${personId}`;
  const boxes: Box[] = [];

  /* -------- The teaching profile -------- */
  if (profile) {
    // A removed profile keeps its place and says it is gone, so the visit's boxes stay where they were (P4).
    const removed = removing.result.state === "confirmed";
    boxes.push(box<PeopleFacultyProfile>({
      id: at("profile"),
      kind: "fact",
      job: copy.profileJob,
      label: copy.profileLabel,
      owner: imam,
      fold: "choice",
      act: OPEN,
      read: removed ? { status: "empty", sentence: copy.profileRemoved } : readOf(detailRead, () => profile),
      prose: (value) => value.title_role || copy.noTitle,
      detail: (value) => value.specialization || copy.profileDetail,
      fields: () =>
        FIELDS.map((field) =>
          savedField(saves, { id: at(`profile-${field}`), field, label: copy[field], theirs: (theirs) => theirs[field] ?? "" }),
        ),
    }));
  }

  /* -------- Teaching assignments -------- */
  const assignments = detail.teaching_assignments;
  if (profile || assignments) {
    boxes.push(box<PeopleDetailResponse>({
      id: at("assign"),
      kind: "list",
      job: copy.assignJob,
      label: copy.assignLabel,
      owner: imam,
      fold: "choice",
      act: OPEN,
      read: assignments?.courses.length ? detailRead : { status: "empty", sentence: copy.noSections },
      detail: (value) => (value.teaching_assignments?.semester_id ? copy.selectedTerm : copy.everyTerm),
      state: (value) => ({ class: "quiet", word: teachingText.sections(value.teaching_assignments?.section_count ?? 0) }),
      rows: (value) => value.teaching_assignments?.courses ?? [],
      row: {
        key: (course) => course.course_id,
        title: (course) => course.course_title,
        facts: assignmentFacts,
        act: (course) => ({ does: "visit", verb: copy.openSection, visit: `course:${course.course_id}` }),
      },
    } as ListBox<PeopleDetailResponse, FacultyCourseLoad>));
  }

  /* -------- Remove teaching profile -------- */
  if (profile) {
    const checked = preview.data;
    const refer = checked?.blockers ?? [];
    const part = at("remove-courses");
    const blockers = (): Blocker[] =>
      !checked
        ? [{ kind: "missing", part, sentence: copy.checkingCourses }]
        : refer.length
          ? [{ kind: "missing", part, sentence: teachingText.clearFirst(refer.length) }]
          : [];
    const effects = [copy.removeEffect];
    boxes.push(box<FacultyRemovalPreview>({
      id: at("remove"),
      kind: "task",
      job: copy.removeJob,
      label: copy.removeLabel,
      owner: imam,
      fold: "choice",
      read: queryRead(preview, copy.previewUnavailable, copy.previewUnavailableDetail),
      detail: () => copy.removeDetail,
      state: () =>
        resultState(
          removing.result,
          // At rest the profile is kept; the courses that hold Remove are said in the open Task (teaching-remove).
          { class: "quiet", word: copy.profileKept },
          { class: "done", word: copy.removed },
        ),
      act: { does: "open", verb: copy.removeVerb },
      candidate: { eligible: removing.result.state !== "confirmed", blocked: null, rank: null },
      parts: [{
        id: part,
        label: copy.referPart,
        state: refer.length ? { class: "waiting", word: teachingText.refer(refer.length) } : { class: "done", word: copy.noneRefer },
        owner: imam,
        fold: "never",
        draft: "none",
        summary: refer.length ? undefined : copy.noneReferSummary,
        render: refer.length
          ? () => (
              <ul className="grid gap-2 text-ui">
                {refer.map((course) => (
                  <li key={course.id} className="[overflow-wrap:anywhere]">
                    {course.label}
                    <span className="text-muted-foreground"> · {courseStatusWord(course.status)}</span>
                  </li>
                ))}
              </ul>
            )
          : undefined,
      }],
      commit: {
        verb: copy.removeVerb,
        cap: copy.removeCap,
        effects: () => effects.map((sentence, index) => ({ id: `effect-${index}`, sentence })),
        blockers,
        run: (key) =>
          removing.run(key, {
            effects,
            remains: [copy.returnRereads],
            write: async (commitKey) => void (await facultyApi.remove(profile.id, commitKey)),
          }),
      },
      result: removing.result,
      draft: false,
    }));
  }

  return boxes;
}
